import React, { useMemo, useState } from "react";
import {
    useGetAdminGarantsQuery,
    useResolveAppealRefundMutation,
    useResolveAppealConfirmMutation,
} from "../api/garants";
import { GarantDeal } from "../api/garants";
import { format } from "date-fns";

const formatTs = (ts?: number) => {
    if (!ts) return "-";
    return format(new Date(ts * 1000), "dd.MM.yyyy HH:mm");
};

const statusLabel = (s: number) => {
    if (s === 3) return "Ожидает";
    if (s === 1 || s === 100) return "Подтверждено";
    if (s === 2) return "Возврат";
    if (s === 4 || s === 5) return "Апелляция";
    return String(s);
};

export const AdminGarantsPage: React.FC = () => {
    const [direction, setDirection] = useState<"incoming" | "outgoing" | "all">(
        "all"
    );
    const [currency, setCurrency] = useState<number | undefined>(undefined);
    const [status, setStatus] = useState<number | undefined>(undefined);

    const { data, isLoading } = useGetAdminGarantsQuery({
        direction: direction === "all" ? undefined : direction,
        currency,
        status,
    });

    const [resolveRefund, { isLoading: isRefunding }] =
        useResolveAppealRefundMutation();
    const [resolveConfirm, { isLoading: isConfirming }] =
        useResolveAppealConfirmMutation();

    const handleResolveRefund = async (id: number) => {
        if (
            !window.confirm(
                "Вы уверены, что хотите вернуть деньги отправителю? Это действие нельзя отменить."
            )
        ) {
            return;
        }
        try {
            await resolveRefund(id).unwrap();
            alert("Деньги возвращены отправителю");
        } catch (error: any) {
            alert(
                error?.data?.message ||
                    "Ошибка при возврате денег отправителю"
            );
        }
    };

    const handleResolveConfirm = async (id: number) => {
        if (
            !window.confirm(
                "Вы уверены, что хотите начислить деньги получателю? Это действие нельзя отменить."
            )
        ) {
            return;
        }
        try {
            await resolveConfirm(id).unwrap();
            alert("Деньги начислены получателю");
        } catch (error: any) {
            alert(
                error?.data?.message ||
                    "Ошибка при начислении денег получателю"
            );
        }
    };

    const rows: GarantDeal[] = useMemo(() => {
        const arr =
            (data?.data?.data as unknown as GarantDeal[]) ??
            (data as any)?.data?.data ??
            [];
        return Array.isArray(arr) ? arr : [];
    }, [data]);

    return (
        <div className="p-6">
            <h1 className="text-2xl font-bold mb-4 dark:text-gray-200">Гарант-сделки</h1>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
                <div>
                    <label className="text-sm text-gray-600 dark:text-gray-200">Направление</label>
                    <select
                        className="w-full border rounded px-2 py-1"
                        value={direction}
                        onChange={(e) =>
                            setDirection(e.target.value as any)
                        }
                    >
                        <option value="all">Все</option>
                        <option value="incoming">Входящие</option>
                        <option value="outgoing">Исходящие</option>
                    </select>
                </div>
                <div>
                    <label className="text-sm text-gray-600 dark:text-gray-200">Валюта</label>
                    <select
                        className="w-full border rounded px-2 py-1"
                        value={currency ?? ""}
                        onChange={(e) =>
                            setCurrency(
                                e.target.value === ""
                                    ? undefined
                                    : Number(e.target.value)
                            )
                        }
                    >
                        <option value="">Все</option>
                        <option value="0">USD</option>
                        <option value="1">USDT</option>
                        <option value="100">USD (garant)</option>
                        <option value="101">USDT (garant)</option>
                    </select>
                </div>
                <div>
                    <label className="text-sm text-gray-600 dark:text-gray-200">Статус</label>
                    <select
                        className="w-full border rounded px-2 py-1"
                        value={status ?? ""}
                        onChange={(e) =>
                            setStatus(
                                e.target.value === ""
                                    ? undefined
                                    : Number(e.target.value)
                            )
                        }
                    >
                        <option value="">Все</option>
                        <option value="3">Ожидает</option>
                        <option value="1">Подтверждено</option>
                        <option value="2">Возврат</option>
                        <option value="4">Апелляция</option>
                    </select>
                </div>
            </div>

            <div className="overflow-auto border rounded">
                <table className="min-w-full text-sm">
                    <thead className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200">
                        <tr>
                            <th className="px-3 py-2 text-left">ID</th>
                            <th className="px-3 py-2 text-left">Пользователь</th>
                            <th className="px-3 py-2 text-left">Сумма</th>
                            <th className="px-3 py-2 text-left">Статус</th>
                            <th className="px-3 py-2 text-left">Описание</th>
                            <th className="px-3 py-2 text-left">Создано</th>
                            <th className="px-3 py-2 text-left">Подтверждено</th>
                            <th className="px-3 py-2 text-left">Трекинг</th>
                            <th className="px-3 py-2 text-left">Апелляция</th>
                            <th className="px-3 py-2 text-left">Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td
                                    colSpan={10}
                                    className="text-center py-6 text-gray-500"
                                >
                                    Загрузка...
                                </td>
                            </tr>
                        ) : rows.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={10}
                                    className="text-center py-6 text-gray-500"
                                >
                                    Нет данных
                                </td>
                            </tr>
                        ) : (
                            rows.map((item) => {
                                const hasAppeal =
                                    item.status === 4 || item.status === 5;
                                return (
                                    <tr key={item.id} className="border-t dark:border-gray-700 dark:text-gray-200">
                                        <td className="px-3 py-2">{item.id}</td>
                                        <td className="px-3 py-2">
                                            {item.user
                                                ? `${item.user.name} ${item.user.surname} (${item.user.id})`
                                                : item.user_id}
                                        </td>
                                        <td className="px-3 py-2">
                                            {item.sum} ({item.currency})
                                        </td>
                                        <td className="px-3 py-2">
                                            {statusLabel(item.status)}
                                        </td>
                                        <td className="px-3 py-2">
                                            {item.additional_desc || "-"}
                                        </td>
                                        <td className="px-3 py-2">
                                            {formatTs(item.stat_time)}
                                        </td>
                                        <td className="px-3 py-2">
                                            {formatTs(item.confirm_time)}
                                        </td>
                                        <td className="px-3 py-2">
                                            {item.tracking || "-"}
                                        </td>
                                        <td className="px-3 py-2">
                                            {item.appeal || "-"}
                                        </td>
                                        <td className="px-3 py-2">
                                            {hasAppeal ? (
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() =>
                                                            handleResolveRefund(
                                                                item.id
                                                            )
                                                        }
                                                        disabled={
                                                            isRefunding ||
                                                            isConfirming
                                                        }
                                                        className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                                    >
                                                        Вернуть отправителю
                                                    </button>
                                                    <button
                                                        onClick={() =>
                                                            handleResolveConfirm(
                                                                item.id
                                                            )
                                                        }
                                                        disabled={
                                                            isRefunding ||
                                                            isConfirming
                                                        }
                                                        className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                                    >
                                                        Начислить получателю
                                                    </button>
                                                </div>
                                            ) : (
                                                "-"
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminGarantsPage;

